import { useEffect, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { io, Socket } from 'socket.io-client'
import config from 'constants/config'
import {
  set as setLocalStorage,
  get as getLocalStorage,
  remove as removeLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { authLogout } from 'actions/AuthActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { isLoggedIn } from 'selectors/accountSelectors'
import useReduxStateListener from 'hooks/useReduxStateListener'
import { accountAccessShowLogin } from 'actions/UiActions'
import { CONNECTION, SESSION, ClientToServerEvents, ServerToClientEvents } from 'api/support'

export interface SessionSocket<T> extends Socket {
  sessionId?: string
  customerId?: string
  events?: T;
}

export type SocketType = SessionSocket<ClientToServerEvents | ServerToClientEvents>;

export default function useSocket() {
  const [socket, setSocket] = useState<SocketType | null>(null)

  const dispatch = useAppDispatch()

  const userIsLoggedIn = useAppSelector(isLoggedIn)
  const userId = useAppSelector((state: App.State) => state.auth.account?.memberId)
  const accessToken = useAppSelector((state: App.State) => state.auth.accessToken)

  const loginListener = useReduxStateListener<{ loggedIn: boolean; firstName?: string; lastName?: string }>((state) => {
    if (isLoggedIn(state)) {
      return { loggedIn: true, firstName: state.auth.account.givenName, lastName: state.auth.account.surname }
    } else if (!state.ui.modal.open) {
      return { loggedIn: false }
    }
  })

  useEffect(() => {
    if (!userIsLoggedIn) {
      removeLocalStorage('supportAssistantSessionId')
      setSocket(null)

      loginListener.awaitResult().then(({ loggedIn }) => {
        if (loggedIn) {
          setSocket(null)
        }
      })
    }

    // show login modal if we log out customer by invalidating session
    if (userIsLoggedIn) {
      loginListener.awaitResult().then(({ loggedIn }) => {
        if (!loggedIn) {
          dispatch(accountAccessShowLogin())
        }
      })
    }
  }, [userIsLoggedIn, loginListener, dispatch])

  useEffect(() => {
    socket?.on(SESSION.CREATED, (socket) => {
      const sessionId = socket.sessionId
      const customerId = socket.customerId

      if (customerId === userId) {
        setLocalStorage('supportAssistantSessionId', sessionId)
      }
    })

    socket?.on(SESSION.EXPIRED, () => {
      removeLocalStorage('supportAssistantSessionId')
      setSocket(null)
    })

    socket?.on(SESSION.INVALID, () => {
      dispatch(authLogout())
      setSocket(null)
    })
  }, [dispatch, socket, userId])

  useEffect(() => {
    const sessionId = getLocalStorage('supportAssistantSessionId')

    if (!socket) {
      const socketIO: SocketType = io(config.API_HOST, {
        transports: ['websocket', 'polling'],
        path: '/api/support/chat/socket.io',
        auth: {
          customerId: userId,
          sessionId,
          accessToken,
        },
      })

      setSocket(socketIO)

      socketIO.on(CONNECTION.ERROR, () => {
        // revert to classic upgrade
        socketIO.io.opts.transports = ['polling', 'websocket']
      })
      return () => {
        socketIO.disconnect()
      }
    }
  }, [socket, userId, accessToken])

  return {
    socket,
    setSocket,
  }
}
